import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  deleteRefundPolicy,
  getCurrentRefundPolicy,
  listRefundPolicies,
  type CurrentRefundPolicyResponse,
  type RefundPolicy,
} from '../lib/api-client';

function isActivePolicy(res: CurrentRefundPolicyResponse | undefined): res is RefundPolicy {
  if (!res) return false;
  if ('id' in res) return true;
  return false;
}

export function RefundPoliciesListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [includeInactive, setIncludeInactive] = useState(false);

  const listQuery = useQuery({
    queryKey: ['refund-policies.list', includeInactive],
    queryFn: () => listRefundPolicies({ include_inactive: includeInactive }),
  });

  const currentQuery = useQuery({
    queryKey: ['refund-policies.current'],
    queryFn: getCurrentRefundPolicy,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRefundPolicy(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.list'] });
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.current'] });
    },
  });

  const handleSoftDelete = (id: string, name: string): void => {
    if (
      window.confirm(`確定要停用政策「${name}」？此操作為軟刪除（active=false），稽核痕跡保留。`)
    ) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">退票政策（B-2 / AC-B2-1）</h1>
        <div className="page-head__actions">
          <Button variant="primary" onClick={() => navigate('/refund-policies/new')}>
            + 新增政策
          </Button>
          <Button
            variant="secondary"
            onClick={() => listQuery.refetch()}
            disabled={listQuery.isFetching}
          >
            {listQuery.isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        時間階梯退費政策（AC-B2-1 launch-blocker P1）；下游退票流程依「目前生效」政策計算退費金額。
      </p>

      {/* 目前生效政策橫幅 */}
      <section style={{ margin: '0 0 18px' }}>
        {currentQuery.isLoading && (
          <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>查詢目前生效政策中…</div>
        )}
        {currentQuery.error && (
          <div className="error-banner">
            無法取得目前生效政策：{(currentQuery.error as Error).message}
          </div>
        )}
        {currentQuery.data && isActivePolicy(currentQuery.data) ? (
          <div
            style={{
              padding: '12px 14px',
              background: 'var(--c-success-light)',
              border: '1px solid #6ee7b7',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--c-success)',
            }}
          >
            <strong>目前生效政策：</strong>
            <span style={{ marginLeft: 8 }}>{currentQuery.data.name}</span>
            <span style={{ marginLeft: 12, fontSize: 12 }}>
              （{currentQuery.data.time_window_rules.length} 條階梯規則； 自{' '}
              {new Date(currentQuery.data.valid_from).toLocaleString('zh-TW')}
              {currentQuery.data.valid_to
                ? ` ~ ${new Date(currentQuery.data.valid_to).toLocaleString('zh-TW')}`
                : ' 起無上限'}
              ）
            </span>
            <Link
              to={`/refund-policies/${currentQuery.data.id}`}
              style={{ marginLeft: 12, fontSize: 13 }}
            >
              檢視
            </Link>
          </div>
        ) : currentQuery.data ? (
          <div
            style={{
              padding: '12px 14px',
              background: 'var(--c-warning-light)',
              border: '1px solid #fcd34d',
              borderRadius: 'var(--radius-sm)',
              color: '#92400e',
            }}
          >
            <strong>注意：</strong>目前無生效中之退票政策；下游退票流程將無法計算退費。
          </div>
        ) : null}
      </section>

      <div style={{ margin: '0 0 12px' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          顯示已停用政策
        </label>
      </div>

      {listQuery.isLoading && <div>載入中…</div>}
      {listQuery.error && <div className="error-banner">{(listQuery.error as Error).message}</div>}
      {deleteMutation.error && (
        <div className="error-banner">停用失敗：{(deleteMutation.error as Error).message}</div>
      )}
      {listQuery.data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>名稱</th>
                <th style={{ textAlign: 'right' }}>階梯數</th>
                <th>最大手續費</th>
                <th>生效期</th>
                <th>狀態</th>
                <th style={{ width: 200 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    尚無退票政策
                  </td>
                </tr>
              )}
              {listQuery.data.items.map((p: RefundPolicy) => {
                const maxFee = p.time_window_rules.reduce(
                  (acc, r) => Math.max(acc, Number(r.fixed_fee)),
                  0,
                );
                return (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/refund-policies/${p.id}`} style={{ fontWeight: 600 }}>
                        {p.name}
                      </Link>
                      {p.description && (
                        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>
                          {p.description}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{p.time_window_rules.length}</td>
                    <td>NT$ {maxFee.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                      {new Date(p.valid_from).toLocaleDateString('zh-TW')}
                      {' ~ '}
                      {p.valid_to ? new Date(p.valid_to).toLocaleDateString('zh-TW') : '無上限'}
                    </td>
                    <td>
                      {p.active ? (
                        <Pill tone="success">啟用</Pill>
                      ) : (
                        <Pill tone="warning">已停用</Pill>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/refund-policies/${p.id}`)}
                        >
                          編輯
                        </Button>
                        {p.active && (
                          <Button
                            variant="secondary"
                            onClick={() => handleSoftDelete(p.id, p.name)}
                            disabled={deleteMutation.isPending}
                          >
                            停用
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
